/**
 * UserBubble Component
 * A reusable avatar component with customizable border color and online glow effect.
 * Replaces the old green dot online indicator with a glowing border when online.
 */
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useNavigate } from "react-router-dom";

// Available bubble colors with their glow variants
export const BUBBLE_COLORS = {
  green: {
    name: 'Green',
    color: '#22c55e',
    glow: '0 0 12px rgba(34, 197, 94, 0.8), 0 0 24px rgba(34, 197, 94, 0.5)'
  },
  blue: {
    name: 'Blue',
    color: '#3b82f6',
    glow: '0 0 12px rgba(59, 130, 246, 0.8), 0 0 24px rgba(59, 130, 246, 0.5)'
  },
  purple: {
    name: 'Purple',
    color: '#a855f7',
    glow: '0 0 12px rgba(168, 85, 247, 0.8), 0 0 24px rgba(168, 85, 247, 0.5)'
  },
  pink: {
    name: 'Pink',
    color: '#ec4899',
    glow: '0 0 12px rgba(236, 72, 153, 0.8), 0 0 24px rgba(236, 72, 153, 0.5)'
  },
  orange: {
    name: 'Orange',
    color: '#f97316',
    glow: '0 0 12px rgba(249, 115, 22, 0.8), 0 0 24px rgba(249, 115, 22, 0.5)'
  },
  yellow: {
    name: 'Gold',
    color: '#eab308',
    glow: '0 0 12px rgba(234, 179, 8, 0.8), 0 0 24px rgba(234, 179, 8, 0.5)'
  },
  red: {
    name: 'Red',
    color: '#ef4444',
    glow: '0 0 12px rgba(239, 68, 68, 0.8), 0 0 24px rgba(239, 68, 68, 0.5)'
  },
  cyan: {
    name: 'Cyan',
    color: '#06b6d4',
    glow: '0 0 12px rgba(6, 182, 212, 0.8), 0 0 24px rgba(6, 182, 212, 0.5)'
  },
  white: {
    name: 'White',
    color: '#ffffff',
    glow: '0 0 12px rgba(255, 255, 255, 0.6), 0 0 24px rgba(255, 255, 255, 0.3)'
  }
};

/**
 * UserBubble - Clickable avatar with colored border and online glow
 * @param {string} userId - User ID for navigation
 * @param {string} picture - User's profile picture URL
 * @param {string} name - User's name (for fallback initial)
 * @param {string} bubbleColor - Color key from BUBBLE_COLORS (default: 'green')
 * @param {boolean} isOnline - Whether user is currently online
 * @param {string} size - Size class (sm, md, lg, xl)
 * @param {boolean} clickable - Whether clicking navigates to profile (default: true)
 * @param {function} onClick - Custom click handler (overrides navigation)
 * @param {string} className - Additional CSS classes
 */
const UserBubble = ({ 
  userId,
  picture, 
  name, 
  bubbleColor = 'green',
  isOnline = false,
  size = 'md',
  clickable = true,
  onClick,
  className = ''
}) => {
  const navigate = useNavigate();
  
  // Get color config
  const colorConfig = BUBBLE_COLORS[bubbleColor] || BUBBLE_COLORS.green;
  
  // Size configurations
  const sizeClasses = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
    '2xl': 'w-20 h-20',
    '3xl': 'w-24 h-24'
  };
  
  const textSizes = {
    xs: 'text-xs',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl'
  };
  
  const borderWidths = {
    xs: '1px',
    sm: '2px',
    md: '2px',
    lg: '2px',
    xl: '3px',
    '2xl': '3px',
    '3xl': '4px'
  };
  
  const handleClick = (e) => {
    // Stop propagation to prevent parent click handlers
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else if (clickable && userId) {
      navigate(`/profile/${userId}`);
    }
  };
  
  // Base shadow for "overlay" effect - makes avatar pop out from content
  const baseShadow = '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)';
  
  const bubbleStyle = {
    border: `${borderWidths[size] || '2px'} solid ${colorConfig.color}`,
    boxShadow: isOnline 
      ? `${colorConfig.glow}, ${baseShadow}` 
      : baseShadow,
    transition: 'box-shadow 0.3s ease, border-color 0.3s ease, transform 0.15s ease',
    position: 'relative',
    zIndex: 10
  };
  
  const Wrapper = clickable ? 'button' : 'div';
  const wrapperProps = clickable ? {
    onClick: handleClick,
    className: `focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--ps-bg-base)] focus:ring-[${colorConfig.color}] rounded-full transition-all hover:scale-110 active:scale-95 flex-shrink-0 ${className}`.trim(),
    'data-testid': `user-bubble-${userId}`,
    'aria-label': `View ${name || 'user'}'s profile`
  } : {
    className: `flex-shrink-0 ${className}`.trim()
  };
  
  return (
    <Wrapper {...wrapperProps}>
      <Avatar 
        className={`${sizeClasses[size] || sizeClasses.md} rounded-full overflow-hidden`}
        style={bubbleStyle}
      >
        <AvatarImage src={picture} className="object-cover" />
        <AvatarFallback 
          className={`bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)] font-semibold ${textSizes[size] || textSizes.md}`}
        >
          {name?.charAt(0)?.toUpperCase() || 'U'}
        </AvatarFallback>
      </Avatar>
    </Wrapper>
  );
};

export default UserBubble;
